import React, { useState, useEffect, useRef } from 'react';
import { FaCloudUploadAlt, FaTrash, FaPlus, FaTimes } from 'react-icons/fa';
import { message, Switch, Slider } from 'antd';
import AdminService from '../services/adminService';

const UNIT_OPTIONS = [
  'None', 'pcs', 'Plate', 'Portion', 'Serving', 'Cup', 'Glass', 'Bowl', 'Slice',
  'g', 'kg', 'ml', 'l', 'pack', 'Packet', 'Bottle', 'Can', 'Box', 'mg', 'dozen', 'bag', 'set', 'Other'
];

const escapeRegex = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const cleanVariantBaseValue = (val, currentUnit) => {
  if (!val) return '';
  let str = String(val).trim();

  // 1. If currentUnit is provided and not 'none', strip it if at end
  if (currentUnit && currentUnit.trim() && currentUnit.toLowerCase() !== 'none') {
    const u = currentUnit.trim();
    const regex = new RegExp(`\\s+${escapeRegex(u)}$`, 'i');
    if (regex.test(str)) {
      const cand = str.replace(regex, '').trim();
      if (cand) str = cand;
    }
  }

  // 2. Check against UNIT_OPTIONS (skip single letters like 'l' unless length > 1, to avoid stripping 'L' for clothing)
  for (const unit of UNIT_OPTIONS) {
    if (unit && unit.toLowerCase() !== 'none' && unit.toLowerCase() !== 'other' && unit.length > 1) {
      const regex = new RegExp(`\\s+${escapeRegex(unit)}$`, 'i');
      if (regex.test(str)) {
        const cand = str.replace(regex, '').trim();
        if (cand) {
          str = cand;
          break;
        }
      }
    }
  }

  return str;
};

const formatVariantSizeLabel = (val, unit) => {
  const base = cleanVariantBaseValue(val, unit);
  const u = (unit || '').trim();
  if (base && u && u.toLowerCase() !== 'none') {
    return `${base} ${u}`;
  }
  return base || (u && u.toLowerCase() !== 'none' ? u : 'Default');
};

const AddProductModal = ({ onClose, onSave, businessId }) => {
  const [formData, setFormData] = useState({
    item_name: '',
    sku: '',
    brand_name: '',
    category: '',
    category_id: '',
    sub_category: '',
    size_label: '',
    description: '',
    net_weight: '',
    unit: '',
    stock: '0',
    original_cost: '',
    gst: '2',
    selling_price: '',
    is_featured: false,
    is_customizable: false,
    is_organic: false,
    color: '',
    gender: '',
    pack_type: '',
    mfg_date: '',
    exp_date: '',
    age_range: [0, 100],
    age_category: '',
    material: '',
    fabric_type: '',
    dim_length: '',
    dim_breadth: '',
    dim_width: '',
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [subImages, setSubImages] = useState([]);
  const [variants, setVariants] = useState([]);
  const [taxSlabs, setTaxSlabs] = useState([]);
  const [apiCategories, setApiCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showSidePanel, setShowSidePanel] = useState(false);

  const fileInputRef = useRef(null);
  const subImageInputRef = useRef(null);

  useEffect(() => {
    AdminService.getTaxSlabs()
      .then((res) => {
        if (res?.success && res?.data) setTaxSlabs(res.data);
        else if (Array.isArray(res)) setTaxSlabs(res);
      })
      .catch(() => { });
  }, []);

  useEffect(() => {
    if (!businessId) return;
    AdminService.getBusinessCategories(businessId)
      .then((res) => {
        if (res?.success && res?.data) setApiCategories(res.data);
        else if (Array.isArray(res)) setApiCategories(res);
      })
      .catch(() => { });
  }, [businessId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const inputValue = type === 'checkbox' ? checked : value;
    setFormData((prev) => {
      const next = { ...prev, [name]: inputValue };
      if (name === 'category') {
        next.sub_category = '';
        const found = apiCategories.find((c) => c.category_name === value);
        next.category_id = found ? (found.category_id || found.id || '') : '';
      }
      return next;
    });
  };

  const handleImageClick = () => fileInputRef.current?.click();

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubImagesChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const newItems = files.map((file) => ({
      id: `${Date.now()}-${Math.random()}`,
      file,
      preview: URL.createObjectURL(file),
    }));
    setSubImages((prev) => [...prev, ...newItems]);
  };

  const handleSubImageClick = () => subImageInputRef.current?.click();

  const removeSubImage = (id) => {
    setSubImages((prev) => prev.filter((item) => item.id !== id));
  };

  const addVariant = () =>
    setVariants((prev) => [
      ...prev,
      { id: Date.now(), size_label: '', net_weight: '', unit: '', pos_plu: '', stock_qty: '0', original_cost: '', selling_price: '', gst: '2', is_visible: true, is_visible_counter: true },
    ]);

  const removeVariant = (id) => setVariants((prev) => prev.filter((v) => v.id !== id));

  const handleVariantChange = (id, field, value) =>
    setVariants((prev) =>
      prev.map((v) => {
        if (v.id !== id) return v;
        if (field === 'unit') {
          const cleanedSizeLabel = cleanVariantBaseValue(v.size_label, v.unit);
          return { ...v, unit: value, size_label: cleanedSizeLabel || v.size_label };
        }
        return { ...v, [field]: value };
      })
    );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.item_name || !formData.selling_price) {
      message.warning('Please fill out all required fields (*)');
      return;
    }

    if (Number(formData.selling_price) <= 0) {
      message.warning('Selling Price must be greater than 0');
      return;
    }

    if (!formData.original_cost || Number(formData.original_cost) <= 0) {
      message.warning('Original Cost must be greater than 0');
      return;
    }

    if (formData.stock !== '' && Number(formData.stock) <= 0) {
      message.warning('Stock Quantity must be greater than 0');
      return;
    }

    if (variants.length > 0) {
      for (let i = 0; i < variants.length; i++) {
        const v = variants[i];
        if (v.selling_price !== '' && Number(v.selling_price) <= 0) {
          message.warning(`Variant ${i + 1}: Selling Price must be greater than 0`);
          return;
        }
        if (!v.original_cost || Number(v.original_cost) <= 0) {
          message.warning(`Variant ${i + 1}: Original Cost must be greater than 0`);
          return;
        }
        if (v.stock_qty !== '' && Number(v.stock_qty) <= 0) {
          message.warning(`Variant ${i + 1}: Stock Quantity must be greater than 0`);
          return;
        }
      }
    }

    if (!formData.category) {
      message.warning('Please select a Category');
      return;
    }

    const selectedCatObj = apiCategories.find(c => (c.category_name || '').trim().toLowerCase() === (formData.category || '').trim().toLowerCase());
    const subCategories = selectedCatObj?.sub_categories || [];
    if (subCategories.length > 0 && !formData.sub_category) {
      message.warning('Please select a Sub Category');
      return;
    }

    const selectedSubCatObj = subCategories.find(sc => (sc.category_name || '').trim().toLowerCase() === (formData.sub_category || '').trim().toLowerCase());
    const categoryId = selectedCatObj ? (selectedCatObj.category_id || selectedCatObj.id || '') : formData.category_id;
    const subCategoryId = selectedSubCatObj ? (selectedSubCatObj.category_id || selectedSubCatObj.id || '') : '';

    setSaving(true);
    const payload = new FormData();
    payload.append('item_name', formData.item_name);
    payload.append('sku', formData.sku);
    payload.append('brand_name', formData.brand_name);
    payload.append('item_category', formData.category);
    payload.append('category_name', formData.category);
    if (categoryId) payload.append('category_id', String(categoryId));
    payload.append('sub_category', formData.sub_category);
    if (subCategoryId) payload.append('sub_category_id', String(subCategoryId));
    payload.append('description', formData.description);
    payload.append('net_weight', formData.net_weight);
    payload.append('unit', formData.unit);
    payload.append('stock', formData.stock);
    payload.append('is_variable', variants.length > 0 ? '1' : '0');
    payload.append('business_id', businessId);

    if (imageFile) payload.append('item_image', imageFile);
    subImages.filter((item) => item.file).forEach((item) => payload.append('sub_images', item.file));
    payload.append('is_featured', formData.is_featured ? '1' : '0');
    payload.append('is_customizable', formData.is_customizable ? '1' : '0');
    payload.append('is_organic', formData.is_organic ? '1' : '0');

    payload.append('mfg_date', formData.mfg_date);
    payload.append('exp_date', formData.exp_date);

    if (formData.category?.toLowerCase() === 'clothing') {
      payload.append('color', formData.color);
      payload.append('gender', formData.gender);
      payload.append('pack_type', formData.pack_type);
      payload.append('age_range', JSON.stringify(formData.age_range));
      payload.append('age_category', formData.age_category);
      payload.append('material', formData.material);
      payload.append('fabric_type', formData.fabric_type);
      payload.append('dim_length', formData.dim_length);
      payload.append('dim_breadth', formData.dim_breadth);
      payload.append('dim_width', formData.dim_width);
    }

    const baseUnit = (formData.unit || '').trim();
    const baseRawVal = cleanVariantBaseValue(formData.size_label, baseUnit);
    const baseCombined = formatVariantSizeLabel(baseRawVal, baseUnit);
    const baseSizeLabelObj = {
      unit: baseUnit,
      value: baseRawVal || baseCombined,
      size_label: baseCombined,
    };

    payload.append('size_label', JSON.stringify(baseSizeLabelObj));
    payload.append('size', JSON.stringify(baseSizeLabelObj));
    payload.append('value', baseRawVal || baseCombined);
    payload.append('unit', baseUnit);

    const baseVariant = {
      unit: baseUnit,
      value: baseRawVal || baseCombined,
      size_label: baseSizeLabelObj,
      size: baseSizeLabelObj,
      net_weight: formData.net_weight || '',
      net_weight_unit: baseUnit,
      pos_plu: '',
      plu_code: '',
      plu: '',
      original_cost: formData.original_cost || '0',
      gst: formData.gst,
      selling_price: formData.selling_price,
      stock_qty: formData.stock || '0',
      color: formData.color || '',
      gender: formData.gender || '',
      is_visible: true,
      is_visible_counter: true,
    };

    const finalVariants =
      variants.length > 0
        ? [
          baseVariant,
          ...variants.map((v) => {
            const vUnit = (v.unit || formData.unit || '').trim();
            const vRawVal = cleanVariantBaseValue(v.value || v.size_label, vUnit);
            const vCombined = formatVariantSizeLabel(vRawVal, vUnit);
            const vSizeLabelObj = {
              unit: vUnit,
              value: vRawVal || vCombined,
              size_label: vCombined,
            };
            return {
              unit: vUnit,
              value: vRawVal || vCombined,
              size_label: vSizeLabelObj,
              size: vSizeLabelObj,
              net_weight: v.net_weight || formData.net_weight || '',
              net_weight_unit: vUnit,
              pos_plu: v.pos_plu || '',
              plu_code: v.pos_plu || '',
              plu: v.pos_plu || '',
              original_cost: v.original_cost || formData.original_cost || '0',
              gst: v.gst || formData.gst,
              selling_price: v.selling_price || formData.selling_price,
              stock_qty: v.stock_qty || formData.stock || '0',
              color: v.color || formData.color || '',
              gender: v.gender || formData.gender || '',
              is_visible: v.is_visible !== undefined ? v.is_visible : true,
              is_visible_counter: v.is_visible_counter !== undefined ? v.is_visible_counter : true,
            };
          }),
        ]
        : [baseVariant];

    payload.append('variants', JSON.stringify(finalVariants));
    payload.append('price', formData.selling_price);
    payload.append('original_cost', formData.original_cost);

    try {
      const res = await AdminService.addProduct(payload);
      if (res.success || res.status === 'success' || (res.data && res.data.success !== false)) {
        message.success('Product added successfully');
        if (onSave) onSave();
        onClose();
      } else {
        message.error('Failed to add product: ' + (res.message || 'Unknown error'));
      }
    } catch (err) {
      message.error('Error adding product: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  // ── Styles ────────────────────────────────────────────────────────────────
  const overlayStyle = {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(0px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999, padding: '20px',
  };
  const modalStyle = {
    background: '#ffffff', color: '#111827', borderRadius: '8px',
    width: '100%', maxWidth: '900px', maxHeight: '90vh',
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
    fontFamily: 'Inter, sans-serif',
  };
  const headerStyle = {
    padding: '20px', borderBottom: '1px solid #e5e7eb',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  };
  const bodyStyle = {
    padding: '20px', overflowY: 'auto', flex: 1,
    display: 'flex', flexDirection: 'column', gap: '20px',
  };
  const sectionStyle = {
    display: 'flex', flexDirection: 'column', gap: '20px',
  };
  const footerStyle = {
    padding: '16px 20px', borderTop: '1px solid #e5e7eb',
    display: 'flex', justifyContent: 'flex-end', gap: '10px',
  };
  const row2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' };
  const row3 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' };
  const ig = { display: 'flex', flexDirection: 'column', gap: '5px' };
  const lbl = { fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'left' };
  const inp = {
    background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px',
    padding: '9px 11px', color: '#111827', fontSize: '14px',
    width: '100%', boxSizing: 'border-box', outline: 'none',
  };
  const uploadBox = {
    border: '1px dashed #d1d5db', borderRadius: '6px', padding: '10px',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', cursor: 'pointer', background: '#f9fafb',
    color: '#3b82f6', gap: '6px', minHeight: '80px',
  };
  const vColStyle = { display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 };
  const vHdr = { fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' };

  const TaxOptions = () =>
    taxSlabs.length > 0
      ? taxSlabs.map((s) => <option key={s.id} value={s.id}>{s.tax_name}</option>)
      : (
        <>
          <option value="1">GST 0%</option>
          <option value="2">GST 5%</option>
          <option value="3">GST 12%</option>
          <option value="4">GST 18%</option>
          <option value="5">GST 28%</option>
        </>
      );

  // variant card: 4 fields per row
  const variantRow4 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' };
  const variantRow3 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' };
  const variantRow2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' };
  const variantHdrStyle = { fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px', textAlign: 'left' };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={headerStyle}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' }}>Add New Product</h2>
          <button
            style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '22px', cursor: 'pointer', padding: 0, lineHeight: 1 }}
            onClick={onClose} aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div style={bodyStyle}>


          {showSidePanel ? (
            <div style={sectionStyle}>
              <div style={ig}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>Images</span>
                  <button
                    type="button"
                    onClick={handleSubImageClick}
                    style={{
                      background: 'transparent', border: '1px solid #3b82f6', color: '#3b82f6',
                      padding: '5px 14px', borderRadius: '6px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', fontWeight: '500',
                    }}
                  >
                    <FaPlus size={10} /> Add Sub Images
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: '10px' }}>
                  <div style={{ ...uploadBox, minHeight: '120px' }} onClick={handleImageClick}>
                    {imagePreview ? (
                      <img src={imagePreview} alt="Preview" style={{ maxHeight: '76px', borderRadius: '4px', objectFit: 'contain' }} />
                    ) : (
                      <>
                        <FaCloudUploadAlt size={22} />
                        <span style={{ fontSize: '12px', fontWeight: '500' }}>Main Image</span>
                      </>
                    )}
                  </div>
                  {subImages.map((img, idx) => (
                    <div key={img.id} style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', background: '#f8fafc', minHeight: '120px' }}>
                      <img src={img.preview} alt={`Sub ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <button
                        type="button"
                        onClick={() => removeSubImage(img.id)}
                        style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(255,255,255,0.85)', border: 'none', borderRadius: '50%', width: '26px', height: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                        aria-label="Remove image"
                      >
                        <FaTimes size={12} color="#ef4444" />
                      </button>
                    </div>
                  ))}
                </div>
                <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="image/*" onChange={handleImageChange} />
                <input type="file" ref={subImageInputRef} style={{ display: 'none' }} accept="image/*" multiple onChange={handleSubImagesChange} />
              </div>

              <div style={row2}>
                <div style={ig}>
                  <label style={lbl}>Manufacturing Date</label>
                  <input type="date" style={inp} name="mfg_date" value={formData.mfg_date} onChange={handleChange} />
                </div>
                <div style={ig}>
                  <label style={lbl}>Expiry Date</label>
                  <input type="date" style={inp} name="exp_date" value={formData.exp_date} onChange={handleChange} />
                </div>
              </div>

              <div style={ig}>
                <span style={{ ...lbl, marginBottom: '8px' }}>Product Options</span>
                <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '30px', justifyContent: 'flex-start', width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Switch checked={formData.is_featured} onChange={(checked) => setFormData(prev => ({ ...prev, is_featured: checked }))} size="small" />
                    <span style={{ fontSize: '13px', color: '#374151' }}>Featured</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Switch checked={formData.is_customizable} onChange={(checked) => setFormData(prev => ({ ...prev, is_customizable: checked }))} size="small" />
                    <span style={{ fontSize: '13px', color: '#374151' }}>Customizable</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Switch checked={formData.is_organic} onChange={(checked) => setFormData(prev => ({ ...prev, is_organic: checked }))} size="small" />
                    <span style={{ fontSize: '13px', color: '#374151' }}>Organic</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div style={sectionStyle}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* Name, SKU & Brand */}
                <div style={row3}>
                  <div style={ig}>
                    <label style={lbl}>Product Name *</label>
                    <input style={inp} name="item_name" value={formData.item_name} onChange={handleChange} placeholder="e.g., Basmati Rice" />
                  </div>
                  <div style={ig}>
                    <label style={lbl}>SKU</label>
                    <input style={inp} name="sku" value={formData.sku} onChange={handleChange} placeholder="Auto-generated if empty" />
                  </div>
                  <div style={ig}>
                    <label style={lbl}>Brand</label>
                    <input style={inp} name="brand_name" value={formData.brand_name} onChange={handleChange} placeholder="e.g., Amul" />
                  </div>
                </div>

                {/* Category, Sub-Category & Size Label */}
                <div style={row3}>
                  {(() => {
                    const selectedCatObj = apiCategories.find(
                      c => (c.category_name || '').trim().toLowerCase() === (formData.category || '').trim().toLowerCase()
                    );
                    const categoryValue = selectedCatObj ? selectedCatObj.category_name : formData.category;
                    const availableSubCategories = selectedCatObj?.sub_categories || [];
                    const selectedSubCatObj = availableSubCategories.find(
                      sc => (sc.category_name || '').trim().toLowerCase() === (formData.sub_category || '').trim().toLowerCase()
                    );
                    const subCategoryValue = selectedSubCatObj ? selectedSubCatObj.category_name : formData.sub_category;
                    const isSubCategoryRequired = availableSubCategories.length > 0;

                    return (
                      <>
                        <div style={ig}>
                          <label style={lbl}>Category *</label>
                          <select style={inp} name="category" value={categoryValue} onChange={handleChange}>
                            <option value="" disabled>Select Category</option>
                            {apiCategories.map((c, i) => (
                              <option key={i} value={c.category_name}>{c.category_name}</option>
                            ))}
                          </select>
                        </div>
                        <div style={ig}>
                          <label style={lbl}>
                            Sub Category {isSubCategoryRequired ? '*' : ''}
                          </label>
                          <select style={inp} name="sub_category" value={subCategoryValue} onChange={handleChange}>
                            <option value="">Select Sub Category</option>
                            {availableSubCategories.map((sc, i) => (
                              <option key={i} value={sc.category_name}>{sc.category_name}</option>
                            ))}
                          </select>
                        </div>
                      </>
                    );
                  })()}
                  <div style={ig}>
                    <label style={lbl}>Size Label</label>
                    <input style={inp} name="size_label" value={formData.size_label} onChange={handleChange} placeholder="e.g., Default, Large" />
                  </div>
                </div>

                {/* Weight / Unit / Stock */}
                <div style={row3}>
                  <div style={ig}>
                    <label style={lbl}>Net Weight</label>
                    <input style={inp} name="net_weight" type="text" value={formData.net_weight} onChange={handleChange} placeholder="e.g., 750" />
                  </div>
                  <div style={ig}>
                    <label style={lbl}>Unit</label>
                    <select style={inp} name="unit" value={formData.unit} onChange={handleChange}>
                      {UNIT_OPTIONS.map((u) => (
                        <option key={u} value={u}>{u}</option>
                      ))}
                    </select>
                  </div>
                  <div style={ig}>
                    <label style={lbl}>Stock</label>
                    <input style={inp} name="stock" type="number" min="1" onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }} value={formData.stock} onChange={handleChange} placeholder="0" />
                  </div>
                </div>

                {/* Pricing (Base Variant) */}
                <div style={row3}>
                  <div style={ig}>
                    <label style={lbl}>Original Cost *</label>
                    <input style={inp} name="original_cost" type="number" min="0.01" step="any" onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }} value={formData.original_cost} onChange={handleChange} placeholder="0.00" />
                  </div>
                  <div style={ig}>
                    <label style={lbl}>Selling Price *</label>
                    <input style={inp} name="selling_price" type="number" min="0.01" step="any" onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }} value={formData.selling_price} onChange={handleChange} placeholder="0.00" />
                  </div>
                  <div style={ig}>
                    <label style={lbl}>GST Tax Slab</label>
                    <select style={inp} name="gst" value={formData.gst} onChange={handleChange}>
                      <TaxOptions />
                    </select>
                  </div>
                </div>

                {formData.category?.toLowerCase() === 'clothing' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '10px' }}>

                    <div style={row3}>
                      <div style={ig}>
                        <label style={lbl}>Color</label>
                        <select style={inp} name="color" value={formData.color} onChange={handleChange}>
                          <option value="">Select Color</option>
                          <option value="red">Red</option>
                          <option value="blue">Blue</option>
                          <option value="black">Black</option>
                          <option value="white">White</option>
                          <option value="green">Green</option>
                          <option value="yellow">Yellow</option>
                        </select>
                      </div>
                      <div style={ig}>
                        <label style={lbl}>Gender</label>
                        <select style={inp} name="gender" value={formData.gender} onChange={handleChange}>
                          <option value="">Select Gender</option>
                          <option value="men">Men</option>
                          <option value="women">Women</option>
                          <option value="unisex">Unisex</option>
                          <option value="kids">Kids</option>
                        </select>
                      </div>
                      <div style={ig}>
                        <label style={lbl}>Pack type</label>
                        <input style={inp} name="pack_type" value={formData.pack_type} onChange={handleChange} placeholder="e.g., Single, Pack of 3" />
                      </div>
                    </div>

                    <div style={ig}>
                      <label style={lbl}>Age Range (yrs)</label>
                      <div style={{ padding: '0 10px' }}>
                        <Slider
                          range
                          min={0}
                          max={100}
                          value={formData.age_range}
                          onChange={(val) => setFormData(prev => ({ ...prev, age_range: val }))}
                          trackStyle={[{ backgroundColor: '#3b82f6' }]}
                          handleStyle={[{ borderColor: '#3b82f6' }, { borderColor: '#3b82f6' }]}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6b7280', marginTop: '-10px' }}>
                          <span>0</span>
                          <span>18</span>
                          <span>45</span>
                          <span>100+</span>
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '16px', alignItems: 'end' }}>
                      <div style={ig}>
                        <label style={lbl}>Age Category (Bucket)</label>
                        <input style={inp} name="age_category" value={formData.age_category} onChange={handleChange} />
                      </div>
                      <div style={ig}>
                        <label style={lbl}>Material (optional)</label>
                        <input style={inp} name="material" value={formData.material} onChange={handleChange} />
                      </div>
                      <div style={ig}>
                        <label style={lbl}>Fabric Type (optional)</label>
                        <input style={inp} name="fabric_type" value={formData.fabric_type} onChange={handleChange} />
                      </div>
                    </div>

                    <div style={{ position: 'relative', marginTop: '10px' }}>
                      <div style={{ borderTop: '1px solid #d1d5db', position: 'absolute', top: '50%', left: 0, right: 0, zIndex: 1 }}></div>
                      <div style={{ position: 'relative', zIndex: 2, background: '#f9fafb', display: 'inline-block', padding: '0 10px', transform: 'translateX(-50%)', color: '#374151', fontSize: '14px', fontWeight: '600' }}>Dimensions (cm)</div>
                    </div>

                    <div style={row3}>
                      <div style={ig}>
                        <label style={lbl}>Length</label>
                        <input style={inp} name="dim_length" value={formData.dim_length} onChange={handleChange} />
                      </div>
                      <div style={ig}>
                        <label style={lbl}>Breadth</label>
                        <input style={inp} name="dim_breadth" value={formData.dim_breadth} onChange={handleChange} />
                      </div>
                      <div style={ig}>
                        <label style={lbl}>Width</label>
                        <input style={inp} name="dim_width" value={formData.dim_width} onChange={handleChange} />
                      </div>
                    </div>

                  </div>
                )}

                {/* Description */}
                <div style={ig}>
                  <label style={lbl}>Description</label>
                  <textarea
                    style={{ ...inp, minHeight: '80px', resize: 'none' }}
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Enter product description..."
                  />
                </div>
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>Variant Details</span>
                  <button
                    type="button"
                    onClick={addVariant}
                    style={{
                      background: 'transparent', border: '1px solid #3b82f6', color: '#3b82f6',
                      padding: '5px 14px', borderRadius: '6px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', fontWeight: '500',
                    }}
                  >
                    <FaPlus size={10} /> Add Variant
                  </button>
                </div>

                {variants.length === 0 ? (
                  <div style={{
                    border: '1px dashed #d1d5db', borderRadius: '8px', padding: '24px',
                    textAlign: 'center', color: '#9ca3af', fontSize: '13px', background: '#f9fafb',
                  }}>
                    <p style={{ margin: 0 }}>No variants added yet.</p>
                    <p style={{ margin: '4px 0 0', fontSize: '12px' }}>Click "Add Variant" to create pack-size or size variations.</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {variants.map((v, idx) => (
                      <div key={v.id} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '14px 14px 10px' }}>
                        {/* Card header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                          <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Variant {idx + 1}</span>
                          <button type="button" onClick={() => removeVariant(v.id)}
                            style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                            <FaTrash size={11} /> Remove
                          </button>
                        </div>
                        {formData.category?.toLowerCase() === 'clothing' ? (
                          <>
                            <div style={variantRow3}>
                              <div style={ig}>
                                <label style={variantHdrStyle}>* SIZE</label>
                                <input style={inp} value={v.size_label}
                                  onChange={(e) => handleVariantChange(v.id, 'size_label', e.target.value)}
                                  placeholder="e.g., XL" />
                              </div>
                              <div style={ig}>
                                <label style={variantHdrStyle}>COLOR</label>
                                <select style={inp} value={v.color || ''} onChange={(e) => handleVariantChange(v.id, 'color', e.target.value)}>
                                  <option value="">Select Color</option>
                                  <option value="red">Red</option>
                                  <option value="blue">Blue</option>
                                  <option value="black">Black</option>
                                  <option value="white">White</option>
                                  <option value="green">Green</option>
                                  <option value="yellow">Yellow</option>
                                </select>
                              </div>
                              <div style={ig}>
                                <label style={variantHdrStyle}>GENDER</label>
                                <select style={inp} value={v.gender || ''} onChange={(e) => handleVariantChange(v.id, 'gender', e.target.value)}>
                                  <option value="">Select Gender</option>
                                  <option value="men">Men</option>
                                  <option value="women">Women</option>
                                  <option value="unisex">Unisex</option>
                                  <option value="kids">Kids</option>
                                </select>
                              </div>
                            </div>
                            <div style={{ ...variantRow2, marginTop: '10px' }}>
                              <div style={ig}>
                                <label style={variantHdrStyle}>* ORIGINAL COST (₹)</label>
                                <div style={{ position: 'relative' }}>
                                  <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280', fontSize: '13px' }}>₹</span>
                                  <input style={{ ...inp, paddingLeft: '22px' }} type="number" value={v.original_cost} min="0.01" step="any" onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                                    onChange={(e) => handleVariantChange(v.id, 'original_cost', e.target.value)} placeholder="0.00" />
                                </div>
                              </div>
                              <div style={ig}>
                                <label style={variantHdrStyle}>* SELLING PRICE (₹)</label>
                                <div style={{ position: 'relative' }}>
                                  <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280', fontSize: '13px' }}>₹</span>
                                  <input style={{ ...inp, paddingLeft: '22px' }} type="number" value={v.selling_price} min="0.01" step="any" onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                                    onChange={(e) => handleVariantChange(v.id, 'selling_price', e.target.value)} placeholder="0.00" />
                                </div>
                              </div>
                            </div>
                            <div style={{ ...variantRow2, marginTop: '10px' }}>
                              <div style={ig}>
                                <label style={variantHdrStyle}>* GST (%)</label>
                                <select style={inp} value={v.gst} onChange={(e) => handleVariantChange(v.id, 'gst', e.target.value)}>
                                  <TaxOptions />
                                </select>
                              </div>
                              <div style={ig}>
                                <label style={variantHdrStyle}>STOCK QTY</label>
                                <input style={inp} type="number" value={v.stock_qty} min="1" onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                                  onChange={(e) => handleVariantChange(v.id, 'stock_qty', e.target.value)}
                                  placeholder="0" />
                              </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'row', gap: '16px', marginTop: '10px' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer' }}>
                                <Switch size="small" checked={v.is_visible !== false} onChange={(checked) => handleVariantChange(v.id, 'is_visible', checked)} />
                                Visible
                              </label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer' }}>
                                <Switch size="small" checked={v.is_visible_counter !== false} onChange={(checked) => handleVariantChange(v.id, 'is_visible_counter', checked)} />
                                Visible Counter
                              </label>
                            </div>
                          </>
                        ) : (
                          <>
                            <div style={variantRow4}>
                              <div style={ig}>
                                <label style={variantHdrStyle}>* Size Label</label>
                                <input style={inp} value={v.size_label}
                                  onChange={(e) => handleVariantChange(v.id, 'size_label', e.target.value)}
                                  placeholder="e.g., Regular" />
                              </div>
                              <div style={ig}>
                                <label style={variantHdrStyle}>Unit</label>
                                <select style={inp} value={v.unit || ''} onChange={(e) => handleVariantChange(v.id, 'unit', e.target.value)}>
                                  {UNIT_OPTIONS.map((u) => <option key={u} value={u === 'None' ? '' : u}>{u}</option>)}
                                </select>
                              </div>
                              <div style={ig}>
                                <label style={variantHdrStyle}>Net Weight</label>
                                <input style={inp} type="number" value={v.net_weight} min="0"
                                  onChange={(e) => handleVariantChange(v.id, 'net_weight', e.target.value)}
                                  placeholder="e.g., 750" />
                              </div>
                              <div style={ig}>
                                <label style={variantHdrStyle}>Stock</label>
                                <input style={inp} type="number" value={v.stock_qty} min="1" onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                                  onChange={(e) => handleVariantChange(v.id, 'stock_qty', e.target.value)}
                                  placeholder="0" />
                              </div>
                            </div>
                            <div style={{ ...variantRow4, marginTop: '10px' }}>
                              <div style={ig}>
                                <label style={variantHdrStyle}>* Original Cost (₹)</label>
                                <div style={{ position: 'relative' }}>
                                  <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280', fontSize: '13px' }}>₹</span>
                                  <input style={{ ...inp, paddingLeft: '22px' }} type="number" value={v.original_cost} min="0.01" step="any" onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                                    onChange={(e) => handleVariantChange(v.id, 'original_cost', e.target.value)} placeholder="0.00" />
                                </div>
                              </div>
                              <div style={ig}>
                                <label style={variantHdrStyle}>* Selling Price (₹)</label>
                                <div style={{ position: 'relative' }}>
                                  <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280', fontSize: '13px' }}>₹</span>
                                  <input style={{ ...inp, paddingLeft: '22px' }} type="number" value={v.selling_price} min="0.01" step="any" onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                                    onChange={(e) => handleVariantChange(v.id, 'selling_price', e.target.value)} placeholder="0.00" />
                                </div>
                              </div>
                              <div style={ig}>
                                <label style={variantHdrStyle}>* GST (%)</label>
                                <select style={inp} value={v.gst} onChange={(e) => handleVariantChange(v.id, 'gst', e.target.value)}>
                                  <TaxOptions />
                                </select>
                              </div>
                              <div style={ig}>
                                <label style={variantHdrStyle}>POS PLU</label>
                                <input style={inp} value={v.pos_plu || ''}
                                  onChange={(e) => handleVariantChange(v.id, 'pos_plu', e.target.value)}
                                  placeholder="Optional PLU code" />
                              </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'row', gap: '16px', marginTop: '10px' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer' }}>
                                <Switch size="small" checked={v.is_visible !== false} onChange={(checked) => handleVariantChange(v.id, 'is_visible', checked)} />
                                Visible
                              </label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer' }}>
                                <Switch size="small" checked={v.is_visible_counter !== false} onChange={(checked) => handleVariantChange(v.id, 'is_visible_counter', checked)} />
                                Visible Counter
                              </label>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <button type="button" onClick={onClose}
            style={{ background: '#f9fafb', border: '1px solid #d1d5db', color: '#374151', padding: '8px 20px', borderRadius: '6px', fontWeight: '500', cursor: 'pointer', fontSize: '14px' }}>
            Cancel
          </button>
          <button
            type="button"
            onClick={() => setShowSidePanel((prev) => !prev)}
            style={{
              background: showSidePanel ? '#eff6ff' : '#2563eb',
              color: showSidePanel ? '#1d4ed8' : '#ffffff',
              border: '1px solid #93c5fd',
              borderRadius: '8px',
              padding: '8px 16px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
            }}
          >
            {showSidePanel ? 'Previous' : 'Next'}
          </button>
          <button type="button" onClick={handleSubmit} disabled={saving}
            style={{ background: saving ? '#9ca3af' : '#3b82f6', border: 'none', color: '#fff', padding: '8px 20px', borderRadius: '6px', fontWeight: '500', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '14px' }}>
            {saving ? 'Adding...' : 'Add Product'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddProductModal;
